// Which guide screenshots suit a highlight. The guide a note links to suits it; so does one whose title or screen names
// share a word with the note. Words every guide in the area shares don't count, nor does the area's own name: every
// Sites guide mentions "site", so matching on it would suggest "Add a site" for any change to sites. Nor do the verbs
// guide titles start with, so "edit anomaly thresholds" doesn't suggest "Edit site details".
import { areaName } from '@/lib/productAreas';
import type { GuideScreenshots } from '@/types/releaseNotes';

export interface SuggestedFrame {
  s3Key: string;
  guide: string;
  name: string | null;
  closeUp: boolean;
}

const STOP_WORDS = new Set(
  [
    'the and for with from your you can now new into when that this are its has have more than all any out our was were will also each get see use one',
    'how what which who their them they there here able let make',
    'add edit find show view open change set manage create update remove delete check choose pick run start'
  ]
    .join(' ')
    .split(' ')
);

const stem = (word: string) =>
  word.endsWith('ies') ? `${word.slice(0, -3)}y` : word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word;

export function words(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/'s\b/g, '')
      .split(/[^a-z0-9]+/)
      .filter(word => word.length >= 3)
      .map(stem)
      .filter(word => !STOP_WORDS.has(word))
  );
}

const overlap = (a: Set<string>, b: Set<string>) => [...a].filter(word => b.has(word)).length;

/**
 * `all` is every screenshot in the area, in guide order. `matches` are the page-sized ones from guides that suit the
 * note, best first: the linked guide, then by words shared with the guide, then with the screen's name. The selected
 * screenshot always leads `matches`, so reopening the picker never hides the current image.
 */
export function suggestFrames(
  guides: GuideScreenshots[],
  item: { area: string; title: string; body: string; guideSlug: string | null },
  selected: string | null = null
): { matches: SuggestedFrame[]; all: SuggestedFrame[] } {
  const guideWords = guides.map(guide => words([guide.title, ...guide.frames.map(f => f.name ?? '')].join(' ')));
  const shared = guides.length > 1 ? [...guideWords[0]].filter(word => guideWords.every(w => w.has(word))) : [];
  const ignored = new Set([...words(`${item.area} ${areaName(item.area)}`), ...shared]);
  const noteWords = new Set([...words(`${item.title} ${item.body}`)].filter(word => !ignored.has(word)));
  const scored = guides.flatMap((guide, g) => {
    const linked = Boolean(item.guideSlug) && guide.slug === item.guideSlug;
    const guideScore = overlap(noteWords, guideWords[g]);
    return guide.frames.map(frame => ({
      frame: { s3Key: frame.s3Key, guide: guide.title, name: frame.name, closeUp: frame.closeUp },
      suits: linked || guideScore > 0,
      score: [frame.s3Key === selected ? 1 : 0, linked ? 1 : 0, guideScore, overlap(noteWords, words(frame.name ?? ''))]
    }));
  });
  const byScore = (a: (typeof scored)[number], b: (typeof scored)[number]) => a.score.map((n, i) => b.score[i] - n).find(diff => diff !== 0) ?? 0;
  return {
    matches: scored
      .filter(s => s.frame.s3Key === selected || (s.suits && !s.frame.closeUp))
      .sort(byScore)
      .map(s => s.frame),
    all: scored.map(s => s.frame)
  };
}
